// src/pages/Ads/components/ReserveComponents/AdsReserveUpdate.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import { HeaderIconL } from "../../../../assets/components/HeaderGroup";
import left from "../../../../assets/icons/Arrow/left.svg";
import { SplitRowHalf, SplitRowQuarter, TwinColumn } from "../../../../assets/components/ButtonGroup";
import { useFixedLayout } from "../../../../hooks/useFixedLayout";
import { STRAIGHT_OPTIONS } from "./straightOptions";
import down from "../../../../assets/icons/Arrow/down_tri.svg"
import { BottomWithButton } from "../../../../assets/components/BottomSheetGroup"
import SectionTitle from "../../../../assets/components/TitleGroup";
import checked from "../../../../assets/icons/Select/radio_checked_color.svg";
import unchecked from "../../../../assets/icons/Select/radio_unchecked.svg";
import Popup from "../../../../assets/components/PopupGroup";

import Reserve1 from "./Reserve1";
import Reserve2 from "./Reserve2";

// 공통 유틸
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const koreanToEnglishDayMap = {
  월: "Mon",
  화: "Tue",
  수: "Wed",
  목: "Thu",
  금: "Fri",
  토: "Sat",
  일: "Sun",
};

// 바로가기 Select 전용 (Update에서만 사용)
const StraightSelect = ({ straightKey, onOpenSheet }) => {
  const selected = STRAIGHT_OPTIONS.find((o) => o.key === straightKey);
  return (
    <div className="p-[16px]">
      <SectionTitle
        title="바로가기 설정"
        subTitle="예약한 시간에 알림을 누르면, 선택한 화면으로 바로 이동해요."
        className="pb-[24px]"
      />

      {/* 바텀시트 여는 버튼 */}
      <button
        type="button"
        className="w-full h-[44px] border border-border-default rounded-[12px] p-[12px] pl-[16px] flex items-center justify-between text-b2 text-text-default bg-surface-light-white"
        onClick={onOpenSheet}
      >
        <span className="truncate">
          {selected ? selected.title : "생성 화면을 선택해 주세요"}
        </span>
        <img src={down} alt="선택" className="w-[16px] h-[16px]" />
      </button>
    </div>
  );
};


const AdsReserveUpdate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialData = location.state?.initialData ?? null;

  // 삭제 팝업 상태
  const [openDelete, setOpenDelete] = useState(false);

  const { headerRef, footerRef, headerSpace, bottomSpace } = useFixedLayout();

  // initialData 없으면 안내 후 메인으로
  useEffect(() => {
    if (!initialData) {
      alert("잘못된 접근입니다.");
      navigate("/ads/main", { state: { tab: "reserve" } });
    }
  }, [initialData, navigate]);

  // ───────── state (항상 훅 먼저!) ─────────
  const [title, setTitle] = useState(() => initialData?.title ?? "");
  const [weeklyRangeValid, setWeeklyRangeValid] = useState(true);
  const [monthlyRangeValid, setMonthlyRangeValid] = useState(true);

  const [repeatCycle, setRepeatCycle] = useState(
    initialData?.repeat_type ?? ""
  );

  const [repeatCount, setRepeatCount] = useState(() => {
    if (initialData) return initialData.repeat_count ?? 0;
    return 0;
  });

  const [start, setStart] = useState(
    initialData?.start_date ? new Date(initialData.start_date) : null
  );
  const [end, setEnd] = useState(
    initialData?.end_date ? new Date(initialData.end_date) : null
  );

  const [selectedWeekdays, setSelectedWeekdays] = useState(() => {
    if (!initialData?.weekly_days) return [];
    const engToKo = {
      Mon: "월",
      Tue: "화",
      Wed: "수",
      Thu: "목",
      Fri: "금",
      Sat: "토",
      Sun: "일",
    };
    return initialData.weekly_days.map((eng) => engToKo[eng] || eng);
  });

  const [selectedDates, setSelectedDates] = useState(
    initialData?.monthly_days ?? []
  );

  const [timeSelections, setTimeSelections] = useState(() =>
    initialData?.repeat_type === "daily" ? initialData.upload_times ?? [] : []
  );
  const [repeatTime, setRepeatTime] = useState(() =>
    initialData?.repeat_type === "daily"
      ? ""
      : initialData?.upload_times?.[0] ?? ""
  );

  const [straightKey, setStraightKey] = useState(() => {
    if (!initialData?.straight) return null;
    const byKey = STRAIGHT_OPTIONS.find(
      (o) => o.key === initialData.straight
    );
    if (byKey) return byKey.key;
    const byTitle = STRAIGHT_OPTIONS.find(
      (o) => o.title === initialData.straight
    );
    return byTitle?.key ?? null;
  });

  const [openMonthlyCalendar, setOpenMonthlyCalendar] = useState(false);
  const [isStraightSheetOpen, setIsStraightSheetOpen] = useState(false);

  // daily일 때 timeSelections 길이 맞추기
  useEffect(() => {
    if (repeatCycle !== "daily") return;
    setTimeSelections((prev) => {
      const next = prev.slice(0, repeatCount);
      while (next.length < repeatCount) next.push("");
      return next;
    });
  }, [repeatCount, repeatCycle]);

  const getEffectiveRepeatCount = () => {
    if (repeatCycle === "daily") return repeatCount;
    if (repeatCycle === "weekly") return selectedWeekdays.length;
    if (repeatCycle === "monthly") return selectedDates.length;
    return 0;
  };

  // ───────── validation & payload ─────────
  const hasStep1 = () => title.trim().length > 0 && title.trim().length <= 20;

  const hasStep2 = () => {
    if (!start || !end) return false;
    if (!repeatCycle) return false;

    if (repeatCycle === "daily") {
      const rc = Number(repeatCount);
      if (!rc || rc < 1) return false;

      const arr = timeSelections.slice(0, rc);
      if (arr.length < rc) return false;
      if (arr.some((t) => !t)) return false;
      return true;
    }

    if (repeatCycle === "weekly") {
      if (selectedWeekdays.length < 1) return false;
      if (!repeatTime) return false;
      if (!weeklyRangeValid) return false;
      return true;
    }

    if (repeatCycle === "monthly") {
      if (selectedDates.length < 1) return false;
      if (!repeatTime) return false;
      if (!monthlyRangeValid) return false;
      return true;
    }

    return false;
  };

  const hasStep3 = () => !!straightKey;

  const buildUploadTimes = () => {
    if (repeatCycle === "daily") {
      return timeSelections.slice(0, repeatCount).filter(Boolean);
    }
    return [repeatTime];
  };

  const buildWeeklyDays = () => {
    if (repeatCycle !== "weekly") return null;
    return selectedWeekdays.map((day) => koreanToEnglishDayMap[day]);
  };

  const buildMonthlyDays = () => {
    if (repeatCycle !== "monthly") return null;
    return selectedDates;
  };

  // ───────── API ─────────
  const handleUpdate = async () => {
    if (!hasStep1() || !hasStep2() || !hasStep3()) {
      alert("모든 예약 정보를 입력해주세요.");
      return;
    }

    const payload = {
      reserve_id: initialData.reserve_id,
      user_id: localStorage.getItem("user_id"),
      title: title.trim(),
      start_date: formatDateLocal(start),
      end_date: formatDateLocal(end),
      repeat_type: repeatCycle,
      repeat_count: getEffectiveRepeatCount(),
      upload_times: buildUploadTimes(),
      weekly_days: buildWeeklyDays(),
      monthly_days: buildMonthlyDays(),
      straight: straightKey,
    };


    // start_date 기준으로 이동할 탭 결정
    //   - 오늘 또는 과거 → running(예약 실행 중)
    //   - 내일 이후     → waiting(예약 대기 중)
    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const startDateOnly = start
      ? new Date(start.getFullYear(), start.getMonth(), start.getDate())
      : startOfToday;

    const initialTab =
      startDateOnly.getTime() > startOfToday.getTime() ? "waiting" : "running";


    try {
      await axios.post(
        `${process.env.REACT_APP_FASTAPI_ADS_URL}/reserve/update`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      navigate("/ads/main", {
        state: {
          tab: "reserve",
          toast: "예약이 수정되었습니다",
          initialTab
        }
      });
    } catch (err) {
      console.error("예약 수정 실패:", err);
      alert("예약 수정에 실패했습니다.");
    }
  };

  // 삭제 버튼 클릭 → 팝업만 열기
  const handleDelete = () => {
    setOpenDelete(true);
  };

  // 삭제 팝업 닫기
  const handleCloseDeletePopup = () => {
    setOpenDelete(false);
  };

  // 삭제 팝업에서 "네, 삭제할래요" 눌렀을 때 실제 삭제
  const handleConfirmDelete = async () => {
    if (!initialData) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_FASTAPI_ADS_URL}/reserve/delete`,
        { reserve_id: initialData.reserve_id },
        { headers: { "Content-Type": "application/json" } }
      );

      // 삭제 후 예약 탭으로 이동  토스트 메시지 전달
      navigate("/ads/main", {
        state: {
          tab: "reserve",
          toast: "예약이 삭제되었습니다",
        },
      });
    } catch (err) {
      console.error("예약 삭제 실패:", err);
      alert("예약 삭제에 실패했습니다.");
    } finally {
      setOpenDelete(false);
    }
  };

  const handleBack = () => {
    navigate("/ads/main", { state: { tab: "reserve" } });
  };

  // initialData 없을 때 보여줄 최소 UI (훅 호출 후 분기라서 OK)
  if (!initialData) {
    return (
      <div className="min-h-screen flex flex-col">
        <div ref={headerRef} className="fixed top-0 left-0 right-0 z-10">
          <HeaderIconL
            icon={left}
            onIconClick={handleBack}
            title="예약 알림 설정"
          />
        </div>
        <div
          className="flex-1 flex items-center justify-center text-b1 text-text-subtler"
          style={{ paddingTop: headerSpace }}
        >
          예약 정보를 불러오고 있습니다…
        </div>
      </div>
    );
  }

  // ───────── 메인 렌더 ─────────
  return (
    <div className="h-dvh flex flex-col">
      <div ref={headerRef} className="fixed top-0 left-0 right-0 z-10">
        <HeaderIconL
          icon={left}
          onIconClick={handleBack}
          title="예약 알림 설정"
        />
      </div>

      <div
        className="flex-1 overflow-auto"
        style={{ paddingTop: headerSpace, paddingBottom: bottomSpace }}
      >
        <Reserve1 title={title} setTitle={setTitle} hasStep1={hasStep1} />

        <div className="py-[16px]">
          <div className="bg-bg-white-subtle h-[16px]" />
        </div>

        <Reserve2
          repeatCycle={repeatCycle}
          setRepeatCycle={setRepeatCycle}
          repeatCount={repeatCount}
          setRepeatCount={setRepeatCount}
          start={start}
          end={end}
          setStart={setStart}
          setEnd={setEnd}
          selectedWeekdays={selectedWeekdays}
          setSelectedWeekdays={setSelectedWeekdays}
          selectedDates={selectedDates}
          setSelectedDates={setSelectedDates}
          timeSelections={timeSelections}
          setTimeSelections={setTimeSelections}
          repeatTime={repeatTime}
          setRepeatTime={setRepeatTime}
          openRepeatOptionSheet={false}
          setOpenRepeatOptionSheet={() => { }}
          openMonthlyCalendar={openMonthlyCalendar}
          setOpenMonthlyCalendar={setOpenMonthlyCalendar}
          autoScroll={false}
          useBlank={false}
          onWeeklyRangeValidChange={setWeeklyRangeValid}
          onMonthlyRangeValidChange={setMonthlyRangeValid}
        />

        <div className="py-[16px]">
          <div className="bg-bg-white-subtle h-[16px]" />
        </div>

        <StraightSelect
          straightKey={straightKey}
          onOpenSheet={() => setIsStraightSheetOpen(true)}
        />
      </div>

      <div ref={footerRef} className="fixed left-0 right-0 bottom-0 z-10">
        <SplitRowHalf
          leftText="삭제"
          leftType="alert"
          onLeft={handleDelete}
          rightText="수정"
          onRight={handleUpdate}
          rightDisabled={!hasStep1() || !hasStep2() || !hasStep3()}
        />
      </div>

      {/* 바로가기 선택 바텀시트 */}
      <BottomWithButton
        height="h-[490px]"
        isOpen={isStraightSheetOpen}
        onClose={() => setIsStraightSheetOpen(false)}
        title="바로가기"
        subtitle={"예약한 시간에 알림을 누르면,\n선택한 화면으로 바로 이동해요."}
        // straightKey 없으면 저장 비활성화
        buttonDisabled={!straightKey}
        // 저장 버튼 동작(여기선 선택만 확정하고 닫기)
        onSubmit={() => {
          setIsStraightSheetOpen(false);
        }}
        // 버튼 레이아웃을 커스텀해서 '닫기 / 저장' 두 개로 만들기
        renderButton={({ onClick, disabled }) => (
          <SplitRowQuarter
            leftText="닫기"
            leftType="empty"
            onLeft={() => setIsStraightSheetOpen(false)}
            rightText="저장"
            onRight={onClick}          // BottomWithButton의 onSubmit 호출
            rightDisabled={disabled}
          />
        )}
      >
        <div className="space-y-[12px] pb-[8px]">
          {STRAIGHT_OPTIONS.map((opt) => {
            const selected = straightKey === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setStraightKey(opt.key)}
                className={`w-full text-left rounded-[16px] border p-[16px] flex gap-[16px] ${selected
                  ? "border-border-primary bg-surface-light-primary-light"
                  : "border-border-default bg-surface-light-white-layered"
                  }`}
              >
                <div className="w-[24px] h-[24px] flex items-center justify-center">
                  <img
                    src={selected ? checked : unchecked}
                    alt="체크"
                  />
                </div>
                <div className="flex-1">
                  <div
                    className={`text-text-default ${selected
                      ? "text-t4"
                      : "text-b1"
                      }`}
                  >{opt.title}</div>
                </div>
              </button>
            );
          })}
        </div>
      </BottomWithButton>

      {/* 삭제 팝업 */}
      <Popup
        isOpen={openDelete}
        onClose={handleCloseDeletePopup}
        title="예약 알림을 삭제하시겠습니까?"
        subtitle={`${initialData.title} 알림을 삭제합니다.`}
        renderButtons={
          <TwinColumn
            topType="alert"
            topText="네, 삭제할래요"
            bottomType="empty"
            bottomText="아니요"
            onTop={handleConfirmDelete}
            onBottom={handleCloseDeletePopup}
          />
        }
      />
    </div>
  );
};

export default AdsReserveUpdate;
